// @flow

import React, { ReactNodeArray, useState, useEffect } from 'react';
import { css } from 'emotion';
import { useLingui } from '../../../app.use/useLingui';
import { ProductItemModel } from '@whop/product/types';
import { systemUI } from '../../../utils.styles/system';
import { Trans } from '@lingui/react';
import { makeStyles, Paper, Tabs, Tab } from '@material-ui/core';
import PackagePickerModal from './PackagePickerModal';
import { useTheme } from '../../../app.use/useTheme';
import { IconRegistry } from '../../../pkg.syncregistry/IconRegistry';

const OFFSET_RIGHT = '25px';
const OFFSET_RIGHT_MOBILE = '10px';

const useStyles = makeStyles((theme) => ({
  card: {
    '&:hover': {
      background: '#CFDCFC4D 0% 0% no-repeat padding-box',
    },
    width: `100%`,
    height: '100%',
    marginRight: OFFSET_RIGHT,
    // marginBottom: '10px',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  showMore: {
    '&:hover': {
      background: '#CFDCFC4D 0% 0% no-repeat padding-box',
    },
    width: `100%`,
    height: '100%',
    marginRight: OFFSET_RIGHT,
    // marginBottom: '10px',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    border: `1px solid #707070`,
    justifyContent: 'center',
    cursor: 'pointer',
    '& span': {
      wordSpacing: '1000px',
      fontWeight: 'bold',
      fontSize: '1.25rem',
      textTransform: 'lowercase',
      textAlign: 'center',
    },
  },
  cardWrapper: {
    display: 'grid',
    gridGap: OFFSET_RIGHT,
    gridTemplateColumns: 'repeat(auto-fit, 137px)',
    gridAutoRows: '137px',
    maxWidth: '800px',
    [theme.breakpoints.down('xs')]: {
      gridAutoRows: '115px',
      gridTemplateColumns: 'repeat(auto-fit, 115px)',
      gridGap: OFFSET_RIGHT_MOBILE,
      justifyContent: 'center',
    },
  },
  header: {
    flexDirection: `column`,
    [theme.breakpoints.up('sm')]: {
      flexDirection: `row`,
    },
  },
  tabPaper: {
    width: `calc(100% - ${OFFSET_RIGHT})`,
    display: 'flex',
    justifyContent: 'center',
    marginTop: '10px',
    [theme.breakpoints.up('sm')]: {
      marginTop: '0px',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  package: {
    border: `1px solid #707070`,
    backgroundColor: '#fff',
    cursor: 'pointer',
    padding: '20px 0 20px 17px',
    [theme.breakpoints.down('xs')]: {
      padding: '10px 0 10px 15px',
    },
  },
  activePackage: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontWeight: 700,
  },
  highlight: {
    color: theme.palette.primary.main,
  },
  underscore: {
    '&::after': {
      display: 'block',
      margin: '5px auto 0 auto',
      height: '2px',
      width: '20px',
      content: '""',
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

const titleStyle = css`
  width: 100%;
  font-size: 1.25rem;
  svg {
    position: relative;
    top: 5px;
  }
`;

export function AmountPicker(props: {
  product: ProductItemModel;
  placeholder?: string;
  quantity: number;
  onChoose: (val: number) => void;
  startFromAmount?: number;

  //PackagePickerModal props
  open: boolean;
  onClose: () => void;
}) {
  const { product, placeholder = '' } = props;

  const theme = useTheme();
  const i18n = useLingui();
  const classes = useStyles();
  // * maxAmountToOrder is the maximum amount of base measure unit
  const { maxAmountToOrder, minAmountToOrder, commonParams } = product;
  const { measure_units = [], measure_unit: mainMeasureUnit } = commonParams;
  const measureUnits = measure_units || [];

  const startFromAmount = props.startFromAmount ? props.startFromAmount : minAmountToOrder;

  const [sellBy, setSellBy] = useState<string>(mainMeasureUnit?.name || '');
  const [packs, setPacks] = useState<ReactNodeArray>([]);

  // * here is an array for grouping all of the units
  const mainSellBy = {
    value: mainMeasureUnit?.name || '',
    label: mainMeasureUnit?.name_verbose || '',
    erpId: mainMeasureUnit?.erp_id || '',
    amount: 1,
    primaryAmount: 1,
  };
  const otherSellBy = measureUnits.map((el) => ({
    value: el?.measure_unit?.name || '',
    label: el?.measure_unit?.name_verbose || '',
    amount: el.measure_unit_amount,
    primaryAmount: el.measure_unit_primary_amount,
    erpId: el?.measure_unit?.erp_id || '',
  }));

  const smallestUnits = otherSellBy
    .filter((el) => el.amount > el.primaryAmount)
    .sort((a, b) => a.amount - b.amount)
    .reverse();
  const smallestUnit = smallestUnits[0];

  const units = smallestUnit ? [mainSellBy, ...otherSellBy] : otherSellBy;
  if (minAmountToOrder > 1) {
    const singlePiece = {
      label: i18n.t`pieces`,
      value: i18n.t`pieces`,
      erpId: 'KS',
      amount: 1,
      primaryAmount: minAmountToOrder,
    };

    units.unshift(singlePiece);
  }
  //* and set up of options for select
  const sellByOpts = units
    .filter((el) => el.amount <= el.primaryAmount)
    .sort((a, b) => a.primaryAmount - b.primaryAmount)
    .map((el) => ({ value: el.value, label: el.label, erpId: el.erpId }));

  const step = 10;
  const maxPackages = maxAmountToOrder;

  const ceiled = Math.ceil((props.quantity + 1) / step) * step;
  const [displayedPackages, setDisplayedPackages] = useState(Math.min(maxPackages, ceiled));

  // const onSellByChange = (val: string) => {
  //   let displayed = displayedPackages;
  //   const currentUnit = units.find((el) => el.value === val);
  //   if (currentUnit) {
  //     let properQuantity = Math.ceil(props.quantity / currentUnit.primaryAmount);
  //     displayed = Math.ceil((properQuantity + 1) / step) * step;
  //   }
  //
  //   setNewPackages(val, displayed);
  //   setSellBy(val);
  // };

  const setNewPackages = (unit: string, displayAmount: number = displayedPackages) => {
    // * clear packages
    setPacks([]);
    const packages: ReactNodeArray = [];

    // * find unit that user wants
    const currentUnit = units.find((el) => el.value === unit);
    if (!currentUnit) return;

    // let singlePiece = false;
    // units.indexOf(currentUnit) === 0 && minAmountToOrder > 1
    //   ? (singlePiece = true)
    //   : (singlePiece = false);

    // * set new ones
    for (let i = startFromAmount; i < maxPackages; i++) {
      if (packages.length + 1 === displayAmount) break;

      let comparisonValue = Math.ceil(props.quantity / currentUnit.primaryAmount);
      let isActive = comparisonValue === i;

      const measureUnitAmount = currentUnit?.amount || 1;
      const primaryUnitAmount = currentUnit?.primaryAmount || 1;
      const name = currentUnit?.value;

      const append = (
        <div
          onClick={() => props.onChoose(primaryUnitAmount * i)}
          key={i}
          className={`${classes.column} ${isActive ? classes.activePackage : ''}`}
          style={{ width: '100%', height: '100%' }}
        >
          {/* first display non primary unit. If current tab is for single pieces this would be excessive */}
          <div
            css={css`
              ${titleStyle};
              margin-bottom: 18px;
            `}
          >
            <systemUI.Text>
              <b>{measureUnitAmount * i}&nbsp;x</b>&nbsp;
              <IconRegistry use="eshop.package" htmlColor={theme.colors.primary} />
              <br />
              {name}
            </systemUI.Text>
          </div>
          {/*
          {name !== mainMeasureUnit?.name && !singlePiece ? (
            <div
              css={css`
                ${titleStyle};
                margin-bottom: 18px;
              `}
            >
              <systemUI.Text>
                <b>{measureUnitAmount * i}&nbsp;x</b>&nbsp;
                <IconRegistry use="eshop.package" htmlColor={theme.colors.primary} />
                <br />
                {name}
              </systemUI.Text>
            </div>
          ) : null}
*/}

          {/* //* then display primary unit */}
          {!smallestUnit && (
            <div css={titleStyle} className={isActive ? '' : classes.highlight}>
              <systemUI.Text weight="bold">
                {primaryUnitAmount * i}&nbsp;{mainMeasureUnit?.name}
              </systemUI.Text>
            </div>
          )}
          {smallestUnit && (
            <div css={titleStyle} className={classes.highlight}>
              <systemUI.Text weight="bold">
                {smallestUnit.amount * i}&nbsp;{smallestUnit.label}
              </systemUI.Text>
            </div>
          )}
        </div>
      );

      packages.push(
        <div key={i} className={`${classes.package} ${classes.card}`}>
          {append}
        </div>
      );
    }

    setPacks(packages);
  };

  const appendTiles = () => {
    let newDisplayedAmount = Math.min(maxPackages, displayedPackages + step);
    setDisplayedPackages(newDisplayedAmount);
    setNewPackages(sellBy, newDisplayedAmount);
  };

  // * use effect is here so we initially call setNewPackages
  useEffect(() => {
    setNewPackages(mainMeasureUnit?.name || '');

    //BAL, KS
    if (mainMeasureUnit?.erp_id === 'BAL') {
      setSellBy(mainMeasureUnit?.name);
      setNewPackages(mainMeasureUnit?.name);
    } else {
      const packageSelectorOpt = sellByOpts.find((sellByOpt) => sellByOpt.erpId === 'BAL');
      if (packageSelectorOpt) {
        setSellBy(packageSelectorOpt.value);
        setNewPackages(packageSelectorOpt.value);
      } else {
        if (sellByOpts.length > 0) {
          setSellBy(sellByOpts[0].value);
          setNewPackages(sellByOpts[0].value);
        }
      }
    }
  }, [mainMeasureUnit?.name]);

  // * we do not know if primary unit is smallest unit so if we find that out we set new value
  // * although this still produces error in console, because of the fact that we are setting sellBy immediately
  // useEffect(() => {
  //   if (!smallestUnit) {
  //     let newSellBy = sellByOpts[0]?.value;
  //     setSellBy(newSellBy);
  //     setNewPackages(newSellBy);
  //   }
  // }, [smallestUnit]);

  if (!mainMeasureUnit) return null;

  return (
    <PackagePickerModal open={props.open} onClose={props.onClose}>
      <Paper style={{ borderRadius: 0 }}>
        <systemUI.FlexColumn responsive={{ padding: ['40px 30px', '60px 50px'] }}>
          <systemUI.FlexRow alignItems="center" className={classes.header}>
            <img
              style={{ height: '120px', width: '120px', flexShrink: 0 }}
              src={product.images.length > 0 ? product.images[0].sizes.medium : placeholder}
              alt={i18n.t`Product`}
            />
            <systemUI.HSpace size={4} />
            <systemUI.FlexColumn justifyContent="space-between" style={{ width: '100%' }}>
              <systemUI.Heading
                responsive={{ fontSize: ['1.25rem !important', '1.75rem !important'] }}
                color="primary"
                css={css`
                  font-weight: 500;
                `}
              >
                <Trans>Choose number of packages for</Trans> <br />
                {product.code} {product.name}
              </systemUI.Heading>
            </systemUI.FlexColumn>
          </systemUI.FlexRow>

          {/*
          {sellByOpts.length > 1 && (
            <systemUI.FlexRow justifyContent={'center'}>
              <Paper className={classes.tabPaper}>
                <Tabs
                  value={sellBy}
                  onChange={(e, newVal) => onSellByChange(newVal)}
                  indicatorColor="primary"
                  textColor="primary"
                  centered
                  scrollButtons="auto"
                >
                  {sellByOpts.map((opt) => {
                    return <Tab key={`tab-${opt.value}`} label={opt.label} value={opt.value} />;
                  })}
                </Tabs>
              </Paper>
            </systemUI.FlexRow>
          )}
*/}

          <systemUI.VSpace size={3} />
          <div className={classes.cardWrapper}>
            {packs}
            {displayedPackages < maxPackages && (
              <div className={`${classes.showMore} ${classes.column}`} onClick={appendTiles}>
                <systemUI.Text weight={'bold'}>
                  <Trans>Add more</Trans>
                </systemUI.Text>
              </div>
            )}
          </div>
        </systemUI.FlexColumn>
      </Paper>
    </PackagePickerModal>
  );
}
